import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { DirectiveCuePlayer, navigationFromMapDirective, toastDurationMs } from '@aeonia/gaia-client';
import type { DirectiveCue, ExperienceNavigation, ExperienceSession, SessionLine } from '@aeonia/gaia-client';

/** Present the active beat's cues; WebSocket receipt never starts a second timeline. */
export default function useDirectiveCues(session: ExperienceSession, active: boolean, line: SessionLine | null) {
  const playerRef = useRef<DirectiveCuePlayer | null>(null);
  if (playerRef.current === null) playerRef.current = new DirectiveCuePlayer();
  const player = playerRef.current;
  const [navigation, setNavigation] = useState<ExperienceNavigation | null>(null);
  const [choice, setChoice] = useState<{stepId?: string; options: string[]}>({options: []});
  const phase = session.state.phase;
  useEffect(() => () => player.dispose(), [player]);

  useEffect(() => {
    if (!active || phase === 'idle' || phase === 'connecting' || phase === 'stopped') {
      setNavigation(null); setChoice({options: []});
      player.play([], 0);
      return;
    }
    if (!line) return;
    const stepId = line.source?.stepId;
    const directives = line.presentationDirectives ?? [];
    setNavigation(previous => {
      let next = stepId && previous?.stepId && stepId !== previous.stepId ? null : previous;
      for (const directive of directives) {
        if (directive.m === 'display.map') next = navigationFromMapDirective(directive, session.state.bindings?.sessionId ?? 'session', stepId);
        if (directive.m === 'display.clear') next = null;
      }
      return next;
    });
    setChoice(previous => {
      let next = (stepId && previous.stepId && stepId !== previous.stepId) || line.source?.response?.awaiting === false
        ? {options: []} : previous;
      for (const directive of directives) {
        if (directive.m === 'display.choice') next = {stepId, options: directive.p.options as string[]};
        if (directive.m === 'display.clear') next = {options: []};
      }
      return next;
    });
    player.play(directives.filter(d => d.m !== 'display.choice' && d.m !== 'display.map'), toastDurationMs(line.text));
  }, [session, player, active, line, phase]);

  const cue: DirectiveCue | null = useSyncExternalStore(
    listener => player.subscribe(listener), () => player.current, () => player.current,
  );
  return {cue, navigation, choice};
}
